import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import {
  BehaviorSubject,
  Subject,
  takeUntil
} from 'rxjs';
import { ConfigurationService, IProviderShareableLinkAppConfig } from '@sondermind/configuration';
import { EmptyURIValue, URIPrefix } from '@sondermind/data-access/directory-link';
import { NavigationKeys } from '@sondermind/iris/dropdown';
import { IconColors, IconSizes } from '@sondermind/iris/icon';
import { MARKETING_LINK } from '@sondermind/constants';
import { BreakpointsService } from '@sondermind/utilities/services';
import { appendAttributionParams } from '@sondermind/utilities/tools';
import { WindowRefService } from '@sondermind/utilities/window';

/**
 * Subset of {@link MARKETING_LINK} keys that this header actually renders.
 * Marketing-attribution params (UTM + click IDs — see
 * `@sondermind/utilities/tools#isAttributionParamKey`) from the current route
 * are appended to these at runtime (see
 * {@link PublicHeaderComponent.buildMarketingLinks}) so attribution survives
 * the navigation back to the marketing site.
 */
const HEADER_MARKETING_LINK_KEYS = [
  'home',
  'whySondermind',
  'therapy',
  'psychiatry',
  'insurance',
  'articles',
  'realResults'
] as const;
type HeaderMarketingLinkKey = typeof HEADER_MARKETING_LINK_KEYS[number];

type MarketingLinks = Record<HeaderMarketingLinkKey, string>;

const initialMarketingLinks = (): MarketingLinks => {
  const out = {} as MarketingLinks;
  HEADER_MARKETING_LINK_KEYS.forEach((key) => {
    out[key] = MARKETING_LINK[key];
  });
  return out;
};

@Component({
  selector: 'public-header',
  templateUrl: './public-header.component.html',
  styleUrls: ['./public-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})

export class PublicHeaderComponent implements OnInit, OnDestroy {
  @Input() set providerPracticeState(state: string) {
    this.providerPracticeStateValue = state;
    this.buildFindProviderUrl();
  }
  @Input() hideFindProviderButton: boolean = false;

  providerPracticeStateValue = '';
  showMobileMenu: boolean = false;
  showExploreCareMenu: boolean = false;
  showLoginMenu: boolean = false;
  isMobile: boolean = false;
  findProviderUrl: string = '';
  ctaButtonText$ = new BehaviorSubject<string>('Find a provider');
  destroyed$ = new Subject<void>();

  iconSizes = IconSizes;
  iconColors = IconColors;
  anonUserId: string = '';
  queryParams: Params | null = null;
  providerPortalLoginUrl = 'https://portal.sondermind.com/login/';
  clientPortalLoginUrl = 'https://client.sondermind.com/';
  /**
   * Initialized to the un-stamped canonical URLs so the template never binds
   * `undefined` to an `[href]` if rendered before `ngOnInit` subscribes to
   * `queryParams` (e.g. SSR pre-hydration paint). Rebuilt as soon as
   * `queryParams` emits.
   */
  marketingLinks: MarketingLinks = initialMarketingLinks();

  constructor(
    public breakpointService: BreakpointsService,
    private route: ActivatedRoute,
    private configurationService: ConfigurationService<IProviderShareableLinkAppConfig>,
    private windowRef: WindowRefService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.setLoginUrls();
    this.route.queryParams.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((queryParams) => {
      if (queryParams['anonUserId']) {
        this.anonUserId = queryParams['anonUserId'] as string;
      }

      this.queryParams = queryParams;
      this.buildFindProviderUrl();
      this.buildMarketingLinks();
      this.cdr.markForCheck();
    });
    this.breakpointService.isMobileOrTablet$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((isMobileOrTablet) => {
      this.isMobile = isMobileOrTablet;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
  setLoginUrls(): void {
    this.providerPortalLoginUrl = this.configurationService.env.providerPortalBase ?
      `${this.configurationService.env.providerPortalBase}/login/` : 'https://portal.sondermind.com/login/';

    this.clientPortalLoginUrl = this.configurationService.env.clientPortalBase ?
      `${this.configurationService.env.clientPortalBase}` : 'https://client.sondermind.com/';
  }

  buildFindProviderUrl(): void {
    const isLinkToProviderDirectory = this.providerPracticeStateValue?.length > 0;

    if (isLinkToProviderDirectory) {
      this.findProviderUrl = this.buildProviderDirectoryUrl();
    } else {
      this.findProviderUrl = this.buildMatchFlowUrl();
    }

    if (this.anonUserId) {
      this.findProviderUrl = `${this.findProviderUrl}&anonUserId=${this.anonUserId}`;
    }
  }

  setShowLoginMenu(val: boolean): void {
    this.showLoginMenu = val;
  }

  private buildMarketingLinks(): void {
    // Iterate the typed header-key set rather than using `Object.fromEntries`
    // + cast — keeps the key/value relationship provable to the type checker
    // so adding a key without updating `HEADER_MARKETING_LINK_KEYS` is a
    // compile error, not silent contract drift.
    const next = {} as MarketingLinks;
    HEADER_MARKETING_LINK_KEYS.forEach((key) => {
      next[key] = appendAttributionParams(MARKETING_LINK[key], this.queryParams);
    });
    this.marketingLinks = next;
  }

  private buildProviderDirectoryUrl(): string {
    const referringUrl = this.queryParams?.['referringUrl'] as string
      ?? encodeURIComponent(this.windowRef.nativeWindow?.location.host || 'localhost:4206');

    const providerDirectoryRoot = this.configurationService.env.providerDirectoryBase
                                  ?? 'https://www.sondermind.com/provider-directory';

    const params = `referringUrl=${referringUrl}`;

    const stateUrl = `${URIPrefix.STATE}-${this.providerPracticeStateValue
      ? this.providerPracticeStateValue.toLocaleLowerCase() : EmptyURIValue}`;

    const cityUrl = `${URIPrefix.CITY}-${EmptyURIValue}`;
    return appendAttributionParams(
      `${providerDirectoryRoot}/${stateUrl}/${cityUrl}?${params}`,
      this.queryParams
    );
  }

  private buildMatchFlowUrl(): string {
    const matchFlowRoot = this.configurationService.env.app?.flowHost
    ?? 'https://start.sondermind.com/';

    const params = `referringUrl=${encodeURIComponent(this.windowRef.nativeWindow?.location.href || 'localhost:4203')}`;

    return appendAttributionParams(`${matchFlowRoot}?${params}`, this.queryParams);
  }

  toggleMobileMenu(): void {
    this.showMobileMenu = !this.showMobileMenu;
  }

  toggleMobileMenuKeyDown(e: KeyboardEvent): void {
    if (e.key === NavigationKeys[' '] || e.key === NavigationKeys.Enter) {
      this.toggleMobileMenu();
    }
  }

  toggleExploreCare(): void {
    this.showExploreCareMenu = !this.showExploreCareMenu;
  }

  setExploreCare(showMenu: boolean): void {
    this.showExploreCareMenu = showMenu;
  }
}
